// Profile Data hook

import { useSelector } from 'react-redux';
import { objectNonEmpty, stringNonEmpty } from '../../../services/objectUtils';
import { FIELDS as F, GROUPS } from '../../../services/consumerFields';

function getCurrentValueImpl(data, changes, field) {
    const value1 = data && data[field];
    const value2 = changes && changes[field];

    const changed = value2 != null && value2 !== value1;
    const value = changed ? value2 : value1;

    return value;
}

function getUpdatePayloadImpl(data, changes) {
    const value = (field) => getCurrentValueImpl(data, changes, field);
    const changed = (field) => changes[field] != null;
    const changedAny = (fields) => fields.some(changed);

    const payload = {};
    const profile = {};
    const consents = {};
    const favouriteBrand = {};

    // Profile
    const lastName = changes[F.LAST_NAME];
    const gender = changes[F.GENDER];
    const email = changes[F.EMAIL];
    const language = changes[F.LANGUAGES];
    const mobile = changes[F.MOBILE_NUMBER];
    const home = data[F.HOME_NUMBER];

    if (lastName != null) profile.lastName = lastName;
    if (gender != null) profile.gender = gender;
    if (email != null) profile.email = email;
    if (language != null) profile.languages = language;

    if (mobile != null) {
        const phones = [];
        if (stringNonEmpty(mobile))
            phones.push({ type: 'mobile', number: mobile });
        if (stringNonEmpty(home)) phones.push({ type: 'home', number: home });
        profile.phones = phones;
    }

    // Brands
    if (changedAny(GROUPS.BRAND_SEGMENTATION)) {
        favouriteBrand.brandFamily = value(F.BRAND_FAMILY) || null;
        favouriteBrand.brandFamilyId = value(F.BRAND_FAMILY_ID) || null;
        favouriteBrand.brandSubFamily = value(F.BRAND_SUB_FAMILY) || null;
        favouriteBrand.brandSubFamilyId = value(F.BRAND_SUB_FAMILY_ID) || null;
    }

    // Profile - address
    if (changedAny(GROUPS.ADDR_PERSONAL)) {
        const address = {
            type: 'personal',
            country: value(F.ADDR_PERSONAL_COUNTRY),
            city: value(F.ADDR_PERSONAL_CITY),
            addressLine1: value(F.ADDR_PERSONAL_LINE1),
            addressLine2: value(F.ADDR_PERSONAL_LINE2) || undefined,
            postal: value(F.ADDR_PERSONAL_POSTAL),
        };
        profile.address = [address];
    }

    // Consents: channels
    const emailOptIn = changes[F.CHANNEL_OPT_EMAIL];
    const smsOptIn = changes[F.CHANNEL_OPT_SMS];
    const callOptin = changes[F.CHANNEL_OPT_CALL];
    const dmOptIn = changes[F.CHANNEL_OPT_DM];

    if (emailOptIn != null) consents.emailOptIn = emailOptIn;
    if (smsOptIn != null) consents.smsOptIn = smsOptIn;
    if (callOptin != null) consents.callOptin = callOptin;
    if (dmOptIn != null) consents.dmOptIn = dmOptIn;

    // Consents: brands
    if (changed(F.BRAND_OPT_FLAG)) {
        const entitlements = [];

        if (value(F.BRAND_OPT_FLAG)) {
            entitlements.push('CHESTERFIELD');
            entitlements.push('MARLBORO');
        }

        consents.brandPreferences = {
            optIn: entitlements.length > 0,
            entitlements,
        };
    }

    // Payload
    if (objectNonEmpty(profile)) payload.profile = profile;
    if (objectNonEmpty(consents)) payload.consents = consents;
    if (objectNonEmpty(favouriteBrand)) payload.favouriteBrand = favouriteBrand;

    return objectNonEmpty(payload) ? payload : null;
}

export function useProfile() {
    const profile = useSelector((state) => state.profile);
    const { data, changes, errors } = profile;

    const hasChanges = () => objectNonEmpty(changes);
    const hasErrors = () => objectNonEmpty(errors);

    const getValue = (field) => data && data[field];
    const getCurrentValue = (field) =>
        getCurrentValueImpl(data, changes, field);
    const getError = (field) => errors && errors[field];
    const isChanged = (field) => changes && changes[field] != null;

    const getUpdatePayload = () => getUpdatePayloadImpl(data, changes);

    return {
        ...profile,
        hasChanges,
        hasErrors,
        getValue,
        getCurrentValue,
        getError,
        isChanged,
        getUpdatePayload,
    };
}
